import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { showToast } from '../../../helpers';
import { NAVIGATE_PATH as PATH } from '../../../helpers/constants';
import {
  addOpsInstructorUnavailability,
  apiMessage,
  createOpsInstructor,
  deactivateOpsInstructorLogin,
  getOpsInstructorConflicts,
  getOpsInstructors,
  getOpsInstructorUnavailability,
  issueOpsInstructorLogin,
  removeOpsInstructorUnavailability,
  seedOpsInstructors,
  TOpsInstructor,
  TOpsInstructorLoginResult,
  TOpsUnmappedSpelling,
  updateOpsInstructor,
} from '../../../requests/opsInstructors';
import {
  btnDanger,
  btnGhost,
  btnPrimary,
  btnSecondary,
  Card,
  EmptyState,
  inputClass,
  labelClass,
  PageBody,
  PageHeader,
  Pill,
  tableClass,
  tdClass,
  theadClass,
  thClass,
  SortTh,
  useListSort,
} from '../ui';

const fmtDay = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '—';

const splitSpellings = (raw: string) =>
  raw
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);

/** The spellings other than the display name, as a muted line. */
const OtherSpellings: React.FC<{ i: TOpsInstructor }> = ({ i }) => {
  const others = i.variants.filter((v) => v !== i.displayName);
  return others.length ? (
    <div className="mt-0.5 text-xs text-gray-bold">
      also as {others.join(' · ')}
    </div>
  ) : null;
};

const LoginPill: React.FC<{ i: TOpsInstructor }> = ({ i }) =>
  i.loginStatus === 'active' ? (
    <Pill value="Active" tone="good" />
  ) : i.loginStatus === 'inactive' ? (
    <Pill value="Inactive" tone="warn" />
  ) : (
    <Pill value="No login" tone="neutral" />
  );

/**
 * The temporary password, shown exactly once. The API never returns it
 * again, so this box is the only place it exists outside the instructor's
 * hands.
 */
const IssuedBox: React.FC<{
  issued: TOpsInstructorLoginResult;
  onDismiss: () => void;
}> = ({ issued, onDismiss }) => {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(issued.temporaryPassword);
      showToast('Password copied', 'success');
    } catch {
      showToast('Could not copy — select it and copy by hand', 'error');
    }
  };
  return (
    <div className="rounded-lg border border-yellow bg-yellow-500 px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-yellow-900">
            {issued.created ? 'Login created' : 'Password reset'} ·{' '}
            {issued.email}
          </div>
          <div className="mt-1 flex items-center gap-3">
            <code className="select-all rounded bg-white px-2.5 py-1 font-mono text-base tracking-wide text-gray-four">
              {issued.temporaryPassword}
            </code>
            <button type="button" className={btnSecondary} onClick={copy}>
              Copy
            </button>
          </div>
          <div className="mt-2 text-xs text-yellow-900">
            Shown once. Hand it to the instructor; they can change it after
            signing in.
          </div>
        </div>
        <button type="button" className={btnGhost} onClick={onDismiss}>
          Done
        </button>
      </div>
    </div>
  );
};

type RowForm = { id: string; kind: 'login' | 'edit' | 'away' };

const fmtWhenShort = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '—';

/**
 * One instructor's away dates, staff side. This is where the PTO block
 * from the Paylocity email is entered (T3.48) — keyed by the identity, so
 * it covers every spelling the schedule uses for the person.
 */
const AwayDatesRow: React.FC<{ i: TOpsInstructor; onClose: () => void }> = ({
  i,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [note, setNote] = useState('PTO');
  const { data, isLoading } = useQuery({
    queryKey: ['opsInstructorUnavailability', i.id],
    queryFn: () => getOpsInstructorUnavailability(i.id),
  });
  const refresh = () => {
    queryClient.invalidateQueries({
      queryKey: ['opsInstructorUnavailability', i.id],
    });
    queryClient.invalidateQueries({ queryKey: ['opsInstructorConflicts'] });
    queryClient.invalidateQueries({ queryKey: ['opsCalendar'] });
  };
  const add = useMutation({
    mutationFn: () =>
      addOpsInstructorUnavailability(i.id, {
        from,
        to: to || undefined,
        note: note.trim() || undefined,
      }),
    onSuccess: (b) => {
      showToast(
        b.conflicts.length
          ? `Saved — ${b.conflicts.length} class${
              b.conflicts.length === 1 ? '' : 'es'
            } booked inside these dates`
          : 'Saved',
        b.conflicts.length ? 'warning' : 'success',
      );
      setFrom('');
      setTo('');
      refresh();
    },
    onError: (err) => showToast(apiMessage(err), 'error'),
  });
  const remove = useMutation({
    mutationFn: (blockId: string) =>
      removeOpsInstructorUnavailability(i.id, blockId),
    onSuccess: refresh,
    onError: (err) => showToast(apiMessage(err), 'error'),
  });
  const items = data?.items ?? [];
  return (
    <div className="grid gap-3">
      <div className="grid gap-3 md:grid-cols-[auto_auto_1fr_auto_auto] md:items-end">
        <div>
          <label className={labelClass}>Away from</label>
          <input
            type="date"
            className={inputClass}
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </div>
        <div>
          <label className={labelClass}>To (inclusive)</label>
          <input
            type="date"
            className={inputClass}
            value={to}
            min={from || undefined}
            onChange={(e) => setTo(e.target.value)}
          />
        </div>
        <div>
          <label className={labelClass}>Note</label>
          <input
            className={`${inputClass} w-full`}
            maxLength={200}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        <button
          className={btnPrimary}
          disabled={!from || add.isPending}
          onClick={() => add.mutate()}
        >
          {add.isPending ? 'Saving…' : 'Add block'}
        </button>
        <button className={btnSecondary} onClick={onClose}>
          Close
        </button>
      </div>
      {isLoading ? (
        <div className="text-xs text-gray-bold">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-xs text-gray-bold">
          No away dates on record for {i.displayName}.
        </div>
      ) : (
        <ul className="divide-y divide-white-three rounded-lg border border-white-three bg-white text-sm">
          {items.map((b) => (
            <li
              key={b.id}
              className="flex flex-wrap items-start justify-between gap-3 px-3 py-2"
            >
              <div>
                <span className="font-medium text-gray-four">
                  {fmtDay(b.from)}
                  {b.to.slice(0, 10) !== b.from.slice(0, 10)
                    ? ` – ${fmtDay(b.to)}`
                    : ''}
                </span>
                {b.note && (
                  <span className="ml-2 text-gray-bold">{b.note}</span>
                )}
                <span className="ml-2 text-[11px] text-gray-two">
                  {b.source === 'outlook'
                    ? 'from Outlook'
                    : `by ${b.createdBy}`}
                </span>
                {b.conflicts.length > 0 && (
                  <div className="mt-0.5 text-xs text-yellow-900">
                    Booked inside:{' '}
                    {b.conflicts
                      .map(
                        (c) =>
                          `${c.name ?? 'class'} · ${fmtWhenShort(c.start)}${
                            c.accountName ? ` · ${c.accountName}` : ''
                          }`,
                      )
                      .join('; ')}
                  </div>
                )}
              </div>
              <button
                className={btnGhost}
                disabled={remove.isPending || b.source === 'outlook'}
                title={
                  b.source === 'outlook'
                    ? 'Mirrors an Outlook event — change it there'
                    : undefined
                }
                onClick={() => remove.mutate(b.id)}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * Instructors — one identity per person, every spelling the data uses for
 * them, and the login that lets them see their own classes. Counts only:
 * how much teaching hangs off a name is what tells you whether the spelling
 * is grouped right, nothing more.
 */
const OpsInstructors: React.FC = () => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['opsInstructors'],
    queryFn: getOpsInstructors,
  });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['opsInstructors'] });

  // New-instructor form
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newVariants, setNewVariants] = useState('');
  const [newEmail, setNewEmail] = useState('');

  // One inline row form at a time
  const [form, setForm] = useState<RowForm | null>(null);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginFirst, setLoginFirst] = useState('');
  const [loginLast, setLoginLast] = useState('');
  const [editName, setEditName] = useState('');
  const [editVariants, setEditVariants] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editActive, setEditActive] = useState(true);

  const [confirmDeactivate, setConfirmDeactivate] = useState('');
  const [issued, setIssued] = useState<TOpsInstructorLoginResult | null>(null);

  // Unmapped spellings: which identity each row would attach to
  const [attachTo, setAttachTo] = useState<Record<string, string>>({});

  const openLogin = (i: TOpsInstructor) => {
    setForm({ id: i.id, kind: 'login' });
    setLoginEmail(i.email ?? '');
    setLoginFirst('');
    setLoginLast('');
    setConfirmDeactivate('');
  };
  const openEdit = (i: TOpsInstructor) => {
    setForm({ id: i.id, kind: 'edit' });
    setEditName(i.displayName);
    setEditVariants(i.variants.filter((v) => v !== i.displayName).join(', '));
    setEditEmail(i.email ?? '');
    setEditActive(i.active);
    setConfirmDeactivate('');
  };

  const seed = useMutation({
    mutationFn: seedOpsInstructors,
    onSuccess: (res) => {
      showToast(
        res.created || res.attached
          ? `${res.created} created, ${res.attached} spellings attached · ${res.total} instructors`
          : `Nothing new — ${res.total} instructors already cover the data`,
        'success',
      );
      refresh();
    },
    onError: (err) => showToast(apiMessage(err), 'error'),
  });

  const create = useMutation({
    mutationFn: createOpsInstructor,
    onSuccess: (res) => {
      showToast(`${res.displayName} added`, 'success');
      setNewOpen(false);
      setNewName('');
      setNewVariants('');
      setNewEmail('');
      refresh();
    },
    onError: (err) => showToast(apiMessage(err), 'error'),
  });

  const update = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Parameters<typeof updateOpsInstructor>[1];
    }) => updateOpsInstructor(id, body),
    onSuccess: () => {
      showToast('Saved', 'success');
      setForm(null);
      refresh();
    },
    onError: (err) => showToast(apiMessage(err), 'error'),
  });

  const issue = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Parameters<typeof issueOpsInstructorLogin>[1];
    }) => issueOpsInstructorLogin(id, body),
    onSuccess: (res) => {
      setIssued(res);
      setForm(null);
      refresh();
    },
    onError: (err) => showToast(apiMessage(err), 'error'),
  });

  const deactivate = useMutation({
    mutationFn: deactivateOpsInstructorLogin,
    onSuccess: (res) => {
      showToast(
        res.deactivated
          ? 'Login deactivated'
          : 'There was no login to deactivate',
        res.deactivated ? 'success' : 'warning',
      );
      setConfirmDeactivate('');
      refresh();
    },
    onError: (err) => showToast(apiMessage(err), 'error'),
  });

  const attach = useMutation({
    mutationFn: ({ i, name }: { i: TOpsInstructor; name: string }) =>
      updateOpsInstructor(i.id, { variants: [...i.variants, name] }),
    onSuccess: (res, vars) => {
      showToast(`"${vars.name}" now belongs to ${res.displayName}`, 'success');
      refresh();
    },
    onError: (err) => showToast(apiMessage(err), 'error'),
  });

  const createFromSpelling = useMutation({
    mutationFn: (name: string) => createOpsInstructor({ displayName: name }),
    onSuccess: (res) => {
      showToast(`${res.displayName} added`, 'success');
      refresh();
    },
    onError: (err) => showToast(apiMessage(err), 'error'),
  });

  const { sortKey, sortDir, toggleSort } = useListSort();
  const items = React.useMemo(() => {
    const rows = [...(data?.items ?? [])];
    if (!sortKey) return rows;
    const val = (i: (typeof rows)[number]): string | number =>
      sortKey === 'name'
        ? i.displayName
        : sortKey === 'classes'
        ? i.classCount
        : sortKey === 'upcoming'
        ? i.upcomingCount
        : sortKey === 'last'
        ? i.lastClass ?? ''
        : sortKey === 'login'
        ? i.loginStatus ?? ''
        : '';
    rows.sort((a, b) => {
      const x = val(a);
      const y = val(b);
      const c =
        typeof x === 'number' && typeof y === 'number'
          ? x - y
          : String(x).localeCompare(String(y));
      return sortDir === 'asc' ? c : -c;
    });
    return rows;
  }, [data, sortKey, sortDir]);
  const unmapped = data?.unmapped ?? [];
  const withLogin = items.filter((i) => i.loginStatus !== 'none').length;

  const { data: conflicts = [] } = useQuery({
    queryKey: ['opsInstructorConflicts'],
    queryFn: getOpsInstructorConflicts,
  });

  const renderForm = (i: TOpsInstructor) => {
    if (form?.kind === 'away') {
      return <AwayDatesRow i={i} onClose={() => setForm(null)} />;
    }
    if (form?.kind === 'login') {
      const reset = i.loginStatus !== 'none';
      return (
        <div className="grid gap-3 md:grid-cols-[1fr_auto_auto_auto] md:items-end">
          <div>
            <label className={labelClass}>Login email</label>
            <input
              className={`${inputClass} w-full`}
              type="email"
              placeholder="name@example.com"
              value={loginEmail}
              onChange={(e) => setLoginEmail(e.target.value)}
            />
          </div>
          <div>
            <label className={labelClass}>First name</label>
            <input
              className={inputClass}
              placeholder={i.displayName.split(' ')[0]}
              value={loginFirst}
              onChange={(e) => setLoginFirst(e.target.value)}
            />
          </div>
          <div>
            <label className={labelClass}>Last name</label>
            <input
              className={inputClass}
              placeholder={i.displayName.split(' ').slice(1).join(' ')}
              value={loginLast}
              onChange={(e) => setLoginLast(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <button
              className={btnPrimary}
              disabled={issue.isPending || !loginEmail.trim().includes('@')}
              onClick={() =>
                issue.mutate({
                  id: i.id,
                  body: {
                    email: loginEmail.trim(),
                    firstName: loginFirst.trim() || undefined,
                    lastName: loginLast.trim() || undefined,
                  },
                })
              }
            >
              {issue.isPending
                ? 'Working…'
                : reset
                ? 'Reset password'
                : 'Issue login'}
            </button>
            <button className={btnSecondary} onClick={() => setForm(null)}>
              Cancel
            </button>
          </div>
          <div className="text-xs text-gray-bold md:col-span-4">
            {reset
              ? 'A new temporary password is issued and the login is switched back on. The old password stops working.'
              : 'Creates a portal login with a temporary password, shown once on the next screen.'}
          </div>
        </div>
      );
    }
    return (
      <div className="grid gap-3 md:grid-cols-[1fr_1.5fr_1fr_auto] md:items-end">
        <div>
          <label className={labelClass}>Display name</label>
          <input
            className={`${inputClass} w-full`}
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
          />
        </div>
        <div>
          <label className={labelClass}>
            Other spellings (comma-separated)
          </label>
          <input
            className={`${inputClass} w-full`}
            placeholder="e.g. J. Smith, Jon Smith"
            value={editVariants}
            onChange={(e) => setEditVariants(e.target.value)}
          />
        </div>
        <div>
          <label className={labelClass}>Email</label>
          <input
            className={`${inputClass} w-full`}
            type="email"
            value={editEmail}
            onChange={(e) => setEditEmail(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          <button
            className={btnPrimary}
            disabled={update.isPending || !editName.trim()}
            onClick={() =>
              update.mutate({
                id: i.id,
                body: {
                  displayName: editName.trim(),
                  variants: [editName.trim(), ...splitSpellings(editVariants)],
                  email: editEmail.trim() || undefined,
                  active: editActive,
                },
              })
            }
          >
            {update.isPending ? 'Saving…' : 'Save'}
          </button>
          <button className={btnSecondary} onClick={() => setForm(null)}>
            Cancel
          </button>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-five md:col-span-4">
          <input
            type="checkbox"
            className="ops-check"
            checked={editActive}
            onChange={(e) => setEditActive(e.target.checked)}
          />
          Active — an inactive identity keeps its classes but cannot open the
          teaching pages
        </label>
      </div>
    );
  };

  return (
    <>
      <PageHeader
        title="Instructors"
        meta={
          data
            ? `${items.length} instructors · ${withLogin} with a login · ${unmapped.length} unmapped spellings`
            : 'One identity per person, every spelling the data uses for them, and their login'
        }
        actions={
          <>
            <button
              className={btnSecondary}
              disabled={seed.isPending}
              onClick={() => seed.mutate()}
              title="Create an identity for every instructor named in the class records, grouping the spellings it can. Safe to run again."
            >
              {seed.isPending ? 'Building…' : 'Build from the data'}
            </button>
            <button
              className={newOpen ? btnSecondary : btnPrimary}
              onClick={() => setNewOpen((o) => !o)}
            >
              {newOpen ? 'Close' : 'New instructor'}
            </button>
          </>
        }
      />
      {newOpen && (
        <div className="px-8 pb-4">
          <Card title="New instructor">
            <div className="grid gap-3 p-5 md:grid-cols-[1fr_1.5fr_1fr_auto] md:items-end">
              <div>
                <label className={labelClass}>Display name</label>
                <input
                  className={`${inputClass} w-full`}
                  placeholder="First Last"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                />
              </div>
              <div>
                <label className={labelClass}>
                  Other spellings (comma-separated)
                </label>
                <input
                  className={`${inputClass} w-full`}
                  placeholder="as the class records write it"
                  value={newVariants}
                  onChange={(e) => setNewVariants(e.target.value)}
                />
              </div>
              <div>
                <label className={labelClass}>Email (optional)</label>
                <input
                  className={`${inputClass} w-full`}
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                />
              </div>
              <button
                className={btnPrimary}
                disabled={create.isPending || !newName.trim()}
                onClick={() =>
                  create.mutate({
                    displayName: newName.trim(),
                    variants: splitSpellings(newVariants),
                    email: newEmail.trim() || undefined,
                  })
                }
              >
                {create.isPending ? 'Adding…' : 'Add'}
              </button>
            </div>
          </Card>
        </div>
      )}
      <PageBody wide>
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="grid gap-5">
            {conflicts.length > 0 && (
              <Card
                title={`Booked during away dates · ${conflicts.length}`}
                right={
                  <span className="text-xs text-gray-bold">
                    The calendar flags these as instructor-unavailable
                  </span>
                }
              >
                <div className="overflow-x-auto">
                  <table className={tableClass}>
                    <thead className={theadClass}>
                      <tr>
                        <th className={thClass}>Instructor</th>
                        <th className={thClass}>Away</th>
                        <th className={thClass}>Class</th>
                        <th className={thClass}>When</th>
                        <th className={thClass}>Account</th>
                        <th className={thClass}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {conflicts.map((c) => (
                        <tr key={`${c.block.id}-${c.class.id}`}>
                          <td
                            className={`${tdClass} font-medium text-gray-four`}
                          >
                            {c.displayName}
                            {c.class.instructor &&
                              c.class.instructor !== c.displayName && (
                                <div className="text-xs text-gray-bold">
                                  as {c.class.instructor}
                                </div>
                              )}
                          </td>
                          <td className={`${tdClass} whitespace-nowrap`}>
                            {fmtDay(c.block.from)}
                            {c.block.to.slice(0, 10) !==
                            c.block.from.slice(0, 10)
                              ? ` – ${fmtDay(c.block.to)}`
                              : ''}
                            {c.block.note && (
                              <div className="text-xs text-gray-bold">
                                {c.block.note}
                              </div>
                            )}
                          </td>
                          <td className={tdClass}>
                            <Link
                              className="text-blue-primary hover:underline"
                              to={PATH.opsClassEdit.replace(':id', c.class.id)}
                            >
                              {c.class.name ?? '(unnamed class)'}
                            </Link>
                            {c.class.type && (
                              <div className="text-xs text-gray-bold">
                                {c.class.type}
                              </div>
                            )}
                          </td>
                          <td className={`${tdClass} whitespace-nowrap`}>
                            {fmtWhenShort(c.class.start)}
                          </td>
                          <td className={tdClass}>
                            {c.class.accountName ?? '—'}
                          </td>
                          <td className={tdClass}>
                            <Pill value={c.class.status} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
            <Card title="Identities">
              {items.length === 0 ? (
                <EmptyState
                  title="No instructors yet"
                  hint="Build from the data creates one identity per instructor named in the class records, grouping the spellings it can."
                  action={
                    <button
                      className={btnPrimary}
                      disabled={seed.isPending}
                      onClick={() => seed.mutate()}
                    >
                      {seed.isPending ? 'Building…' : 'Build from the data'}
                    </button>
                  }
                />
              ) : (
                <div className="overflow-x-auto">
                  <table className={tableClass}>
                    <thead className={theadClass}>
                      <tr>
                        <SortTh
                          k="name"
                          label="Instructor"
                          sort={sortKey}
                          dir={sortDir}
                          onSort={toggleSort}
                        />
                        <SortTh
                          k="classes"
                          label="Classes"
                          sort={sortKey}
                          dir={sortDir}
                          onSort={toggleSort}
                          right
                        />
                        <SortTh
                          k="upcoming"
                          label="Upcoming"
                          sort={sortKey}
                          dir={sortDir}
                          onSort={toggleSort}
                          right
                        />
                        <SortTh
                          k="last"
                          label="Last class"
                          sort={sortKey}
                          dir={sortDir}
                          onSort={toggleSort}
                        />
                        <SortTh
                          k="login"
                          label="Login"
                          sort={sortKey}
                          dir={sortDir}
                          onSort={toggleSort}
                        />
                        <th className={`${thClass} text-right`}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((i) => {
                        const isOpen = form?.id === i.id;
                        const confirming = confirmDeactivate === i.id;
                        return (
                          <React.Fragment key={i.id}>
                            <tr className={i.active ? '' : 'opacity-60'}>
                              <td className={tdClass}>
                                <div className="font-medium text-gray-four">
                                  {i.displayName}
                                  {!i.active && (
                                    <span className="ml-2 text-[11px] font-medium uppercase tracking-wide text-gray-two">
                                      inactive
                                    </span>
                                  )}
                                </div>
                                <OtherSpellings i={i} />
                              </td>
                              <td className={`${tdClass} text-right`}>
                                {i.classCount ? (
                                  <Link
                                    className="text-blue-primary hover:underline"
                                    to={`${PATH.opsClasses}?instructor=${
                                      i.id
                                    }&when=all&label=${encodeURIComponent(
                                      i.displayName,
                                    )}`}
                                  >
                                    {i.classCount.toLocaleString()}
                                  </Link>
                                ) : (
                                  '—'
                                )}
                              </td>
                              <td className={`${tdClass} text-right`}>
                                {i.upcomingCount ? (
                                  <Link
                                    className="text-blue-primary hover:underline"
                                    to={`${PATH.opsClasses}?instructor=${
                                      i.id
                                    }&when=upcoming&label=${encodeURIComponent(
                                      i.displayName,
                                    )}`}
                                  >
                                    {i.upcomingCount.toLocaleString()}
                                  </Link>
                                ) : (
                                  '—'
                                )}
                              </td>
                              <td
                                className={`${tdClass} whitespace-nowrap text-gray-three`}
                              >
                                {fmtDay(i.lastClass)}
                              </td>
                              <td className={tdClass}>
                                <LoginPill i={i} />
                                {i.email && (
                                  <div className="mt-0.5 text-xs text-gray-bold">
                                    {i.email}
                                  </div>
                                )}
                              </td>
                              <td className={`${tdClass} text-right`}>
                                <span className="inline-flex flex-wrap justify-end gap-1">
                                  {confirming ? (
                                    <>
                                      <button
                                        className={`${btnDanger} px-2.5 py-1 text-xs`}
                                        disabled={deactivate.isPending}
                                        onClick={() => deactivate.mutate(i.id)}
                                      >
                                        {deactivate.isPending
                                          ? 'Deactivating…'
                                          : 'Confirm deactivate'}
                                      </button>
                                      <button
                                        className={btnGhost}
                                        onClick={() => setConfirmDeactivate('')}
                                      >
                                        Cancel
                                      </button>
                                    </>
                                  ) : (
                                    <>
                                      <button
                                        className={btnGhost}
                                        onClick={() =>
                                          isOpen && form?.kind === 'login'
                                            ? setForm(null)
                                            : openLogin(i)
                                        }
                                      >
                                        {i.loginStatus === 'none'
                                          ? 'Issue login'
                                          : 'Reset password'}
                                      </button>
                                      {i.loginStatus === 'active' && (
                                        <button
                                          className={btnGhost}
                                          onClick={() => {
                                            setForm(null);
                                            setConfirmDeactivate(i.id);
                                          }}
                                        >
                                          Deactivate
                                        </button>
                                      )}
                                      <button
                                        className={btnGhost}
                                        onClick={() => {
                                          setConfirmDeactivate('');
                                          isOpen && form?.kind === 'away'
                                            ? setForm(null)
                                            : setForm({
                                                id: i.id,
                                                kind: 'away',
                                              });
                                        }}
                                      >
                                        Away dates
                                      </button>
                                      <button
                                        className={btnGhost}
                                        onClick={() =>
                                          isOpen && form?.kind === 'edit'
                                            ? setForm(null)
                                            : openEdit(i)
                                        }
                                      >
                                        Edit
                                      </button>
                                    </>
                                  )}
                                </span>
                              </td>
                            </tr>
                            {issued?.id === i.id && (
                              <tr>
                                <td
                                  colSpan={6}
                                  className="bg-grey-50 px-4 py-3"
                                >
                                  <IssuedBox
                                    issued={issued}
                                    onDismiss={() => setIssued(null)}
                                  />
                                </td>
                              </tr>
                            )}
                            {isOpen && (
                              <tr>
                                <td
                                  colSpan={6}
                                  className="bg-grey-50 px-4 py-4"
                                >
                                  {renderForm(i)}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            <Card
              title="Unmapped spellings"
              right={
                <span className="text-xs text-gray-bold">
                  Names in the class records that no identity owns yet
                </span>
              }
            >
              {unmapped.length === 0 ? (
                <EmptyState
                  title="Every spelling belongs to someone"
                  hint="New names show up here as classes are scheduled."
                />
              ) : (
                <div className="overflow-x-auto">
                  <table className={tableClass}>
                    <thead className={theadClass}>
                      <tr>
                        <th className={thClass}>Spelling</th>
                        <th className={`${thClass} text-right`}>Classes</th>
                        <th className={`${thClass} text-right`}>Upcoming</th>
                        <th className={`${thClass} text-right`}>Assignments</th>
                        <th className={thClass}>Attach to…</th>
                        <th className={`${thClass} text-right`}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {unmapped.map((u: TOpsUnmappedSpelling) => {
                        const choice = attachTo[u.name] ?? u.suggestedFor ?? '';
                        const target = items.find((i) => i.id === choice);
                        return (
                          <tr key={u.name}>
                            <td
                              className={`${tdClass} font-medium text-gray-four`}
                            >
                              {u.name}
                              {u.resource && (
                                <span className="ml-2 text-[11px] font-medium uppercase tracking-wide text-gray-two">
                                  resource
                                </span>
                              )}
                            </td>
                            <td className={`${tdClass} text-right`}>
                              {u.classCount.toLocaleString()}
                            </td>
                            <td className={`${tdClass} text-right`}>
                              {u.upcomingCount.toLocaleString()}
                            </td>
                            <td className={`${tdClass} text-right`}>
                              {u.assignmentCount.toLocaleString()}
                            </td>
                            <td className={tdClass}>
                              <select
                                className={`${inputClass} w-56 py-1 text-xs`}
                                value={choice}
                                onChange={(e) =>
                                  setAttachTo((m) => ({
                                    ...m,
                                    [u.name]: e.target.value,
                                  }))
                                }
                              >
                                <option value="">Choose an instructor…</option>
                                {items.map((i) => (
                                  <option key={i.id} value={i.id}>
                                    {i.displayName}
                                    {i.id === u.suggestedFor
                                      ? ' (suggested)'
                                      : ''}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className={`${tdClass} text-right`}>
                              <span className="inline-flex gap-1">
                                <button
                                  className={btnGhost}
                                  disabled={!target || attach.isPending}
                                  onClick={() =>
                                    target &&
                                    attach.mutate({ i: target, name: u.name })
                                  }
                                >
                                  Attach
                                </button>
                                <button
                                  className={btnGhost}
                                  disabled={createFromSpelling.isPending}
                                  onClick={() =>
                                    createFromSpelling.mutate(u.name)
                                  }
                                >
                                  Create identity
                                </button>
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>
        )}
      </PageBody>
    </>
  );
};

export default OpsInstructors;
